import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { VisionAnalysis } from '../lib/vision';
import type { QuestionBox } from '../lib/vision-types';

// Shared state for an in-progress worksheet. Lives at the /app route layer so it survives
// navigation between capture, review, answer, and export screens. Resets when the user
// returns to the app home or finishes a worksheet.

interface WorksheetState {
  image: string | null;
  analysis: VisionAnalysis | null;
  answers: Record<string, string>;
  /** Answer boxes the pupil moved or resized in the placement editor (normalised, keyed by question id). */
  placements: Record<string, QuestionBox>;
  /** Index of the question currently being answered. */
  cursor: number;
}

interface WorksheetContextValue {
  state: WorksheetState;
  setImage: (image: string | null) => void;
  setAnalysis: (analysis: VisionAnalysis | null) => void;
  setAnswer: (questionId: string, text: string) => void;
  setPlacement: (questionId: string, box: QuestionBox) => void;
  clearPlacement: (questionId: string) => void;
  setCursor: (cursor: number) => void;
  reset: () => void;
}

const initialState: WorksheetState = {
  image: null,
  analysis: null,
  answers: {},
  placements: {},
  cursor: 0,
};

const WorksheetContext = createContext<WorksheetContextValue | null>(null);

export function WorksheetProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WorksheetState>(initialState);

  const setImage = useCallback((image: string | null) => {
    // A new photo means old placements no longer line up with anything.
    setState((s) => ({ ...s, image, placements: {} }));
  }, []);

  const setAnalysis = useCallback((analysis: VisionAnalysis | null) => {
    setState((s) => ({ ...s, analysis, cursor: 0 }));
  }, []);

  const setAnswer = useCallback((questionId: string, text: string) => {
    setState((s) => ({ ...s, answers: { ...s.answers, [questionId]: text } }));
  }, []);

  const setPlacement = useCallback((questionId: string, box: QuestionBox) => {
    setState((s) => ({ ...s, placements: { ...s.placements, [questionId]: box } }));
  }, []);

  const clearPlacement = useCallback((questionId: string) => {
    setState((s) => {
      const { [questionId]: _removed, ...rest } = s.placements;
      return { ...s, placements: rest };
    });
  }, []);

  const setCursor = useCallback((cursor: number) => {
    setState((s) => ({ ...s, cursor: Math.max(0, cursor) }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const value = useMemo<WorksheetContextValue>(
    () => ({ state, setImage, setAnalysis, setAnswer, setPlacement, clearPlacement, setCursor, reset }),
    [state, setImage, setAnalysis, setAnswer, setPlacement, clearPlacement, setCursor, reset]
  );

  return <WorksheetContext.Provider value={value}>{children}</WorksheetContext.Provider>;
}

export function useWorksheet(): WorksheetContextValue {
  const ctx = useContext(WorksheetContext);
  if (!ctx) {
    throw new Error('useWorksheet must be used inside <WorksheetProvider>.');
  }
  return ctx;
}
